import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from '../../contexts/AuthContext';

const { FiMessageSquare, FiLogOut, FiUser, FiUsers, FiBarChart2, FiMenu, FiX, FiChevronDown } = FiIcons;

const Header = ({ connectionStatus }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigateTo = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  return (
    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 md:px-6 md:py-4 shadow-md">
      <div className="container mx-auto flex items-center justify-between">
        {/* Logo and Title */}
        <div className="flex items-center space-x-2 md:space-x-3">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-white p-2 rounded-lg hover:bg-white/20"
          >
            <SafeIcon icon={mobileMenuOpen ? FiX : FiMenu} className="text-xl" />
          </button>
          <div 
            className="flex items-center space-x-2" 
            onClick={() => navigate('/dashboard')}
            style={{ cursor: 'pointer' }}
          >
            <SafeIcon icon={FiMessageSquare} className="text-white text-xl md:text-2xl" />
            <h1 className="text-lg md:text-2xl font-bold text-white">AI Agent Monitoring</h1>
          </div>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-6">
          <button
            onClick={() => navigateTo('/dashboard')}
            className="text-white hover:text-blue-100 flex items-center space-x-1"
          >
            <SafeIcon icon={FiMessageSquare} />
            <span>Conversations</span>
          </button>
          <button
            onClick={() => navigateTo('/analytics')}
            className="text-white hover:text-blue-100 flex items-center space-x-1"
          >
            <SafeIcon icon={FiBarChart2} />
            <span>Analytics</span>
          </button>
          {user && user.role === 'admin' && (
            <button
              onClick={() => navigateTo('/users')}
              className="text-white hover:text-blue-100 flex items-center space-x-1"
            >
              <SafeIcon icon={FiUsers} />
              <span>Users</span>
            </button>
          )}
        </div>

        {/* User and Connection Status */}
        <div className="flex items-center space-x-3">
          <div className={`hidden md:block text-xs md:text-sm px-2 md:px-3 py-1 rounded-full ${
            connectionStatus?.connected 
              ? 'bg-green-500/20 text-green-100' 
              : 'bg-red-500/20 text-red-100'
          }`}>
            {connectionStatus?.connected ? 'Connected' : 'Disconnected'}
          </div>

          {user && (
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-1 text-white bg-white/10 hover:bg-white/20 py-1 px-2 md:py-1.5 md:px-3 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiUser} className="text-sm" />
                <span className="hidden md:inline text-sm">{user.full_name || user.username}</span>
                <span className="hidden md:inline text-xs px-1.5 py-0.5 bg-white/20 rounded-full">
                  {user.role === 'admin' ? 'Admin' : 'Viewer'}
                </span>
                <SafeIcon icon={FiChevronDown} className="text-xs" />
              </button>

              {showUserMenu && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-50"
                >
                  <div className="px-4 py-2 border-b">
                    <div className="font-medium">{user.full_name || user.username}</div>
                    <div className="text-xs text-gray-500">
                      {user.role === 'admin' ? 'Administrator' : 'Viewer'}
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <SafeIcon icon={FiLogOut} className="text-gray-400" />
                    <span>Logout</span>
                  </button>
                </motion.div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="md:hidden mt-3 pt-3 border-t border-white/20"
        >
          <div className="flex flex-col space-y-2">
            <button
              onClick={() => navigateTo('/dashboard')}
              className="text-white hover:bg-white/10 py-2 px-3 rounded-lg flex items-center space-x-2"
            >
              <SafeIcon icon={FiMessageSquare} />
              <span>Conversations</span>
            </button>
            <button
              onClick={() => navigateTo('/analytics')}
              className="text-white hover:bg-white/10 py-2 px-3 rounded-lg flex items-center space-x-2"
            >
              <SafeIcon icon={FiBarChart2} />
              <span>Analytics</span>
            </button>
            {user && user.role === 'admin' && (
              <button
                onClick={() => navigateTo('/users')}
                className="text-white hover:bg-white/10 py-2 px-3 rounded-lg flex items-center space-x-2"
              >
                <SafeIcon icon={FiUsers} />
                <span>Users</span>
              </button>
            )}
            <div className="pt-2 mt-2 border-t border-white/20 flex justify-between items-center">
              <div className={`text-xs px-3 py-1 rounded-full ${
                connectionStatus?.connected 
                  ? 'bg-green-500/20 text-green-100' 
                  : 'bg-red-500/20 text-red-100'
              }`}>
                {connectionStatus?.connected ? 'Connected' : 'Disconnected'}
              </div>
              <button
                onClick={handleLogout}
                className="text-white hover:bg-white/10 py-1 px-3 rounded-lg flex items-center space-x-2"
              >
                <SafeIcon icon={FiLogOut} />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default Header;